/**
 * Design System Yoco - Tokens Couleurs
 * Style: Clean, neutre, professionnel
 *
 * REGLES:
 * - Utiliser grey pour tous les neutres
 * - primary = corail/rouge pour actions et accents
 * - Status colors = success/warning/error/info
 */

// ==============================================
// GREY - Echelle neutre pure (pas de bleu, pas de beige)
// ==============================================
export const grey = {
  50:  '#FAFAFA',  // Background page
  100: '#F4F4F5',  // Background hover, muted
  200: '#E4E4E7',  // Borders light
  300: '#D4D4D8',  // Borders
  400: '#A1A1AA',  // Text disabled, icons muted
  500: '#71717A',  // Text secondary
  600: '#52525B',  // Text secondary dark
  700: '#3F3F46',  // Text primary light
  800: '#27272A',  // Text primary
  900: '#18181B',  // Text darkest
};

// ==============================================
// PRIMARY - Bleu (identite visuelle)
// ==============================================
export const primary = {
  50:  '#EFF6FF',  // Background tres light
  100: '#DBEAFE',  // Background light
  200: '#BFDBFE',  // Background medium
  300: '#93C5FD',  // Light accent
  400: '#60A5FA',  // Medium accent
  500: '#5FB1F9',  // Main brand color
  600: '#4A9CE8',  // Hover state
  700: '#3B8DD9',  // Active/pressed state
};

// ==============================================
// STATUS COLORS
// ==============================================
export const success = {
  50:  '#F0FDF4',
  100: '#DCFCE7',
  main: '#22C55E',
  dark: '#16A34A',
};

export const warning = {
  50:  '#FFFBEB',
  100: '#FEF3C7',
  main: '#F59E0B',
  dark: '#D97706',
};

export const error = {
  50:  '#FEF2F2',
  100: '#FEE2E2',
  main: '#EF4444',
  dark: '#DC2626',
};

export const info = {
  50:  '#EFF6FF',
  100: '#DBEAFE',
  main: '#3B82F6',
  dark: '#2563EB',
};

// ==============================================
// BADGES - Tags et labels
// ==============================================
export const badges = {
  // Bibliotheque (recettes globales partagees)
  library: {
    bg: '#F0F9FF',
    text: '#0369A1',
    border: '#BAE6FD',
  },
  // Personnel (recettes du coach)
  personal: {
    bg: '#F0FDF4',
    text: '#15803D',
    border: '#BBF7D0',
  },
  // Categories de repas
  breakfast: {
    bg: '#FEF3C7',
    text: '#B45309',
    border: '#FDE68A',
  },
  lunch: {
    bg: '#DBEAFE',
    text: '#1D4ED8',
    border: '#BFDBFE',
  },
  dinner: {
    bg: '#F3E8FF',
    text: '#7C3AED',
    border: '#DDD6FE',
  },
  snack: {
    bg: '#FCE7F3',
    text: '#BE185D',
    border: '#FBCFE8',
  },
};

// ==============================================
// WEEKPLAN - Styles pour la vue tableau planning
// ==============================================
export const weekplan = {
  // Header du tableau
  header: {
    bg: grey[50],
    text: grey[600],
    border: grey[200],
  },
  // Cellule vide
  emptyCell: {
    bg: grey[50],
    text: grey[400],
    border: grey[200],
    borderStyle: 'dashed',
  },
  // Cellule remplie
  filledCell: {
    bg: '#FFFFFF',
    text: grey[800],
    border: grey[200],
  },
  // Cellule hover
  hoverCell: {
    bg: primary[50],
    border: primary[300],
  },
  // Types de jour
  dayType: {
    OFF: {
      bg: grey[100],
      text: grey[600],
      chip: grey[200],
    },
    ON: {
      bg: primary[50],
      text: primary[600],
      chip: primary[200],
    },
    INTENSIF: {
      bg: error[50],
      text: error.dark,
      chip: error[100],
    },
  },
  // Indicateur calories
  calories: {
    underTarget: success.main,
    onTarget: primary[500],
    overTarget: warning.main,
  },
  // Labels des repas
  mealLabel: {
    bg: grey[50],
    text: grey[600],
  },
};

// ==============================================
// SEMANTIC TOKENS - Utiliser dans les composants
// ==============================================
export const colors = {
  // Brand
  brand: primary[500],
  brandHover: primary[600],
  brandActive: primary[700],
  brandLight: primary[50],

  // Text
  textPrimary: grey[800],
  textSecondary: grey[500],
  textTertiary: grey[400],
  textDisabled: grey[400],
  textInverse: '#FFFFFF',

  // Backgrounds
  bgPage: '#FFFFFF',
  bgSurface: '#FFFFFF',
  bgMuted: grey[100],
  bgHover: grey[100],
  bgActive: grey[200],

  // Borders
  border: grey[200],
  borderStrong: grey[300],
  borderFocus: primary[500],

  // Sidebar (fond blanc, pas gris)
  sidebarBg: '#FFFFFF',
  sidebarText: grey[800],
  sidebarTextMuted: grey[500],
  sidebarTextActive: grey[900],
  sidebarIconDefault: grey[400],
  sidebarIconActive: primary[500],
  sidebarHover: grey[100],
  sidebarActive: primary[50],
  sidebarActiveBorder: primary[500],
  sidebarBorder: grey[200],

  // Cards
  cardBg: '#FFFFFF',
  cardBorder: grey[200],

  // Inputs
  inputBg: '#FFFFFF',
  inputBorder: grey[300],
  inputBorderHover: grey[400],
  inputBorderFocus: primary[500],
  inputPlaceholder: grey[400],

  // Table
  tableHeaderBg: grey[50],
  tableHeaderText: grey[500],
  tableBorder: grey[200],
  tableRowHover: grey[50],
};

const tokens = { grey, primary, success, warning, error, info, badges, weekplan, colors };
export default tokens;
